import { Grid, Select, Textarea, TextInput } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import dayjs from "configs/dayjs";
import { WAREHOUSE_TYPE } from "constants/common/warehouse";
import { FormikProps, getIn } from "formik";
import { memo, useEffect, useMemo } from "react";
import { useListWarehouse } from "utils/hooks/request/useWarehouses";
import { POCreateFormValues } from "../../config";

const spanHalf = { base: 12, md: 6 };

interface PurchaseOrderGeneralFieldsProps {
  formik: FormikProps<POCreateFormValues>;
}

const PurchaseOrderGeneralFields = memo(
  ({ formik }: PurchaseOrderGeneralFieldsProps) => {
    const { warehouses, warehouseOptions } = useListWarehouse();

    // Derive a stable primitive so the effect below only reruns when the
    // resolved main warehouse actually changes, not on every SWR refetch.
    const mainWarehouseId = useMemo(
      () =>
        warehouses?.find((warehouse) => warehouse.type === WAREHOUSE_TYPE.MAIN)
          ?.id,
      [warehouses],
    );

    useEffect(() => {
      if (!formik.values.warehouseId && mainWarehouseId) {
        formik.setFieldValue("warehouseId", mainWarehouseId);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainWarehouseId]);

    const getFieldError = (field: keyof POCreateFormValues) => {
      const touched = getIn(formik.touched, field);
      const error = getIn(formik.errors, field);
      return touched && error ? (error as string) : null;
    };

    return (
      <Grid>
        <Grid.Col span={spanHalf}>
          <TextInput
            label="Mã đơn hàng"
            placeholder="VD: PO-001"
            {...formik.getFieldProps("code")}
            error={getFieldError("code")}
          />
        </Grid.Col>

        <Grid.Col span={spanHalf}>
          <DateTimePicker
            label="Ngày tạo"
            placeholder="Chọn ngày tạo"
            withAsterisk
            value={
              formik.values.createdAt
                ? dayjs.unix(formik.values.createdAt).toDate()
                : null
            }
            onChange={(value) =>
              formik.setFieldValue(
                "createdAt",
                value ? dayjs(value).unix() : undefined,
              )
            }
            onBlur={() => formik.setFieldTouched("createdAt", true)}
            error={getFieldError("createdAt")}
          />
        </Grid.Col>

        <Grid.Col span={spanHalf}>
          <Select
            label="Kho nhập"
            placeholder="Chọn kho"
            withAsterisk
            data={warehouseOptions ?? []}
            value={formik.values.warehouseId}
            onChange={(value) => formik.setFieldValue("warehouseId", value)}
            onBlur={() => formik.setFieldTouched("warehouseId", true)}
            error={getFieldError("warehouseId")}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <Textarea
            label="Ghi chú"
            placeholder="Nhập ghi chú (tuỳ chọn)"
            autosize
            minRows={2}
            {...formik.getFieldProps("notes")}
            error={getFieldError("notes")}
          />
        </Grid.Col>
      </Grid>
    );
  },
  (prev, next) =>
    prev.formik.values.code === next.formik.values.code &&
    prev.formik.values.createdAt === next.formik.values.createdAt &&
    prev.formik.values.warehouseId === next.formik.values.warehouseId &&
    prev.formik.values.notes === next.formik.values.notes &&
    prev.formik.errors.code === next.formik.errors.code &&
    prev.formik.errors.createdAt === next.formik.errors.createdAt &&
    prev.formik.errors.warehouseId === next.formik.errors.warehouseId &&
    prev.formik.errors.notes === next.formik.errors.notes &&
    prev.formik.touched.code === next.formik.touched.code &&
    prev.formik.touched.createdAt === next.formik.touched.createdAt &&
    prev.formik.touched.warehouseId === next.formik.touched.warehouseId &&
    prev.formik.touched.notes === next.formik.touched.notes,
);

PurchaseOrderGeneralFields.displayName = "PurchaseOrderGeneralFields";

export default PurchaseOrderGeneralFields;
